/**
 * Toplu haciz ekraninda maas haczi rozeti/basligi ve gonderim kapisi icin borclunun aktif SGK kaydi. (DEV-3353)
 *
 * UYAP'in "SGK Haciz" sorgusu (BorcluSgkHaciz) kisinin SGK'da herhangi bir kaydi varsa
 * sorguSonucu=true donuyor; GSS ve "Diger" kayitlari da buna giriyor. Bu yuzden 4/a kaydi hic
 * olmayan, kamu kaydi pasif olan borclu icin bile "SGK kaydi var" yaziyor ve sigorta panelindeki
 * "SSK pasif" ile celisiyordu (TDT-3494).
 *
 * Maas haczinin konusu calisilan yer ya da baglanmis aylik oldugu icin durum, maas haczi
 * secildiginde TopluHacizHazirlaTaskHandler'in zaten yaptigi sigorta sorgularindan cikarilir:
 * SSK (4/a) aktif ve son isyerinden cikis yok, kamu (4/c) pasif degil ya da SSK / Bag-Kur emekli ayligi
 * odeniyor. Kurum
 * borcluda ya da bu sorgularin hicbiri yapilamamissa karar verilemez; o zaman eskisi gibi SGK Haciz
 * sonucu kullanilir.
 */

interface SigortaSorgusu {
    sorguSonucu?: boolean;
    sorguSonucDVO?: any;
    message?: string;
}

export interface MaasHacziSigortaBorclu {
    isKisiBool?: boolean;
    ssk?: SigortaSorgusu;
    sgkkamu?: SigortaSorgusu;
    emekliSsk?: SigortaSorgusu;
    emekliBagkur?: SigortaSorgusu;
    sgkhaciz?: SigortaSorgusu;
    haciz?: { maasHacziSelected?: boolean };
}

export interface MaasHacziSigortaDurumu {
    aktif: boolean;
    message: string;
}

// DosyaBorcluSSK'daki SigortaDurumKod.AKTIF; o modul "@/main"i cektigi icin ice alinmadi.
const SSK_AKTIF_DURUM_KODU = "A";
// SgkKamu hataKodu 995 "kayit bulunamadi"; sifirdan farkli diger kodlar servis hatasi.
const KAMU_KAYIT_BULUNAMADI_KODU = 995;
// SSK isyeri hizmet sorgusu: "Bu yil ve bir onceki yil icin 4a hizmet verisi bulunamadi."
const SSK_HIZMET_YOK_KODU = 6;

function kucukHarf(metin: unknown): string {
    return typeof metin === "string" ? metin.toLocaleLowerCase("tr") : "";
}

// Sorgu hic yapilamadiysa alan bos EmptyObject ({message}) kaliyor. Kimlik bilgisi eksikse (kurum
// borcluda oldugu gibi) UYAP sorguSonucu=false ile birlikte cevap yerine duz hata metni donduruyor.
function sorgulandi(sorgu?: SigortaSorgusu): boolean {
    return typeof sorgu?.sorguSonucu === "boolean" && typeof sorgu.sorguSonucDVO !== "string";
}

// SSK'da hata metni bir alt seviyede geliyor: sorguSonucDVO.sskBilgi.
function sskSorgulandi(sorgu?: SigortaSorgusu): boolean {
    return sorgulandi(sorgu) && typeof sorgu?.sorguSonucDVO?.sskBilgi !== "string";
}

function sskSigortaliBilgi(sorgu?: SigortaSorgusu): any {
    return sorgu?.sorguSonucDVO?.sskBilgiDetay?.sgkIsyeriDVO?.sigortaliBilgi;
}

function sskHizmetYok(sorgu?: SigortaSorgusu): boolean {
    return sorgu?.sorguSonucDVO?.isYeriBilgileri?.data?.sonuc?.hataKod == SSK_HIZMET_YOK_KODU;
}

// durumKod "A" tek basina calisiyor demek degil: gercek veride A kayitlarinin yarisinda istenCikisTarihi var ve
// donemYilAy cikis ayina esit, yani kayit o isyerinde bitmis. hataKod 6 da son iki yilda 4/a hizmeti olmadigini soyler.
function sskAktif(sorgu?: SigortaSorgusu): boolean {
    const bilgi = sskSigortaliBilgi(sorgu);
    return bilgi?.durumKod == SSK_AKTIF_DURUM_KODU && !bilgi?.istenCikisTarihi && !sskHizmetYok(sorgu);
}

// Sigorta paneli bu kayitlara durumKod'a bakip yine "SSK aktif" yazdigi icin rozet nedenini soyler.
// UYAP'tan gelen tarih v-html ile basildigi icin yalniz gg/aa/yyyy biciminde alinir.
function sskNotu(sorgu?: SigortaSorgusu): string | undefined {
    const bilgi = sskSigortaliBilgi(sorgu);
    if (bilgi?.durumKod != SSK_AKTIF_DURUM_KODU)
        return undefined;
    const cikis = bilgi?.istenCikisTarihi;
    if (typeof cikis === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(cikis))
        return "SSK: işten çıkış " + cikis;
    if (cikis)
        return "SSK: işten çıkış var";
    if (sskHizmetYok(sorgu))
        return "SSK: son iki yılda 4/a hizmeti yok";
    return undefined;
}

// durumKapsami4C "Aktif", "Pasif", "K.Pasif" gibi geliyor; pasif yazmayan kayit aktif sayilir.
function kamuAktif(sorgu?: SigortaSorgusu): boolean {
    const sonuc = sorgu?.sorguSonucDVO;
    return sorgu?.sorguSonucu === true && sonuc?.hataKodu == 0 && !kucukHarf(sonuc?.durumKapsami4C).includes("pasif");
}

// Servis hatasi "kayit yok" sayilmasin diye yalniz basarili cevap ya da 995 sorgulanmis kabul edilir.
function kamuSorgulandi(sorgu?: SigortaSorgusu): boolean {
    return sorgu?.sorguSonucu === true || sorgu?.sorguSonucDVO?.hataKodu == KAMU_KAYIT_BULUNAMADI_KODU;
}

// Bag-Kur emekli cevabi aylik satirlarini da tasiyor; "Aylik Odenmez" satiri haczedilecek aylik degil.
function emekliAyligiVar(sorgu?: SigortaSorgusu): boolean {
    if (sorgu?.sorguSonucu !== true)
        return false;
    const ayliklar = sorgu.sorguSonucDVO?.emekliKaydi4b?.aylikGoruntuleForm;
    if (!Array.isArray(ayliklar))
        return true;
    return ayliklar.some((aylik: any) => !kucukHarf(aylik?.onayDurumuString).includes("ödenmez"));
}

interface SigortaKaynagi {
    alan: "ssk" | "sgkkamu" | "emekliSsk" | "emekliBagkur";
    aktifEtiketi: string;
    sorguEtiketi: string;
    aktifMi: (sorgu?: SigortaSorgusu) => boolean;
    sorgulandiMi: (sorgu?: SigortaSorgusu) => boolean;
}

// emekliKamu bilerek yok (DEV-3514): queryBorcluEmekliKamu artik gercek Kamu emekli servisini cagiriyor ama basarili
// cevabin sekli bilinmiyor (HAR yok), aylik odenip odenmedigi okunamaz. Eski gorevlerde alan hala SskEmekli kopyasi;
// kaynak sayilirsa ayni SSK emekli kaydi bir de "Kamu emeklisi" diye yazilir.
const KAYNAKLAR: SigortaKaynagi[] = [
    {alan: "ssk", aktifEtiketi: "SSK çalışanı", sorguEtiketi: "SSK", aktifMi: sskAktif, sorgulandiMi: sskSorgulandi},
    {alan: "sgkkamu", aktifEtiketi: "Kamu çalışanı", sorguEtiketi: "Kamu", aktifMi: kamuAktif, sorgulandiMi: kamuSorgulandi},
    {alan: "emekliSsk", aktifEtiketi: "SSK emeklisi", sorguEtiketi: "SSK emekli", aktifMi: emekliAyligiVar, sorgulandiMi: sorgulandi},
    {alan: "emekliBagkur", aktifEtiketi: "Bağ-Kur emeklisi", sorguEtiketi: "Bağ-Kur emekli", aktifMi: emekliAyligiVar, sorgulandiMi: sorgulandi},
];

// Hic sorgu sonucu yoksa mesaj bos kalmasin: talep formu "Gonderilsin"in neden kapali geldigini buradan gosteriyor.
function sgkHacizSonucu(borclu: MaasHacziSigortaBorclu): MaasHacziSigortaDurumu {
    const sgkHaciz = borclu?.sgkhaciz;
    return {aktif: !!sgkHaciz?.sorguSonucu, message: sgkHaciz?.message || "SGK durumu sorgulanamadı"};
}

export function maasHacziSigortaDurumu(borclu: MaasHacziSigortaBorclu): MaasHacziSigortaDurumu {
    if (borclu?.isKisiBool === false)
        return sgkHacizSonucu(borclu);

    const aktifler = KAYNAKLAR.filter(k => k.aktifMi(borclu?.[k.alan])).map(k => k.aktifEtiketi);
    if (aktifler.length > 0)
        return {aktif: true, message: "SGK'da aktif çalışan/emekli kaydı var: " + aktifler.join(", ")};

    const sorgulanamayanlar = KAYNAKLAR.filter(k => !k.sorgulandiMi(borclu?.[k.alan])).map(k => k.sorguEtiketi);
    if (sorgulanamayanlar.length == KAYNAKLAR.length)
        return sgkHacizSonucu(borclu);
    const notlar: string[] = [];
    const sskNot = sskNotu(borclu?.ssk);
    if (sskNot)
        notlar.push(sskNot);
    if (sorgulanamayanlar.length > 0)
        notlar.push("sorgulanamayan: " + sorgulanamayanlar.join(", "));
    let message = "SGK'da aktif çalışan/emekli kaydı yok";
    if (notlar.length > 0)
        message += " (" + notlar.join("; ") + ")";
    return {aktif: false, message};
}

// Toplu haciz hazirlanirken "Gonderilsin" kutusunun ilk degeri: aktif calisan/emekli kaydi yoksa kapali gelir,
// kullanici isterse acar. Kurumda ve sigorta sorgusu yokken SGK Haciz sonucuna duser (eski davranis).
export function maasHacziVarsayilanGonderilsinMi(borclu: MaasHacziSigortaBorclu): boolean {
    return maasHacziSigortaDurumu(borclu).aktif;
}

// Talep listesi degisince dosya yeniden sorgulanip borclu nesneleri sifirdan kuruluyor; kullanicinin "Gonderilsin"
// secimi kaybolmasin diye onceki listeden kisiKurumId ile toplanir. Yalniz atanmis (boolean) degerler alinir.
export function maasHacziSecimleriniTopla(borclular?: Array<{ kisiKurumId?: number, haciz?: { maasHacziSelected?: boolean } }>): Map<number, boolean> {
    const secimler = new Map<number, boolean>();
    (borclular || []).forEach(borclu => {
        const secim = borclu?.haciz?.maasHacziSelected;
        if (borclu?.kisiKurumId != null && typeof secim === "boolean")
            secimler.set(borclu.kisiKurumId, secim);
    });
    return secimler;
}

// Kapi eskiden yalniz SGK Haciz sonucuna bakiyordu; o sorgu "yok" dediginde ya da kota bittiginde SSK'si
// aktif borclunun maas haczi, rozet yesilken sessizce atlaniyordu. Eski kosul korunur, aktif kayit da yeter.
export function maasHacziGonderilsinMi(borclu: MaasHacziSigortaBorclu): boolean {
    if (borclu?.haciz?.maasHacziSelected === false)
        return false;
    return !!borclu?.sgkhaciz?.sorguSonucu || maasHacziSigortaDurumu(borclu).aktif;
}
